import React, { useEffect, useState, useCallback } from 'react';

const CountdownTimer = ({ targetDate, onComplete }) => {
  const calculateTimeLeft = useCallback(() => {
    const target = targetDate ? new Date(targetDate) : new Date('2026-01-01T00:00:00');
    const difference = target - new Date();
    
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      expired: false
    };
  }, [targetDate]);

  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft());
  const [isExpired, setIsExpired] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showPartyPopup, setShowPartyPopup] = useState(false);
  const [partyEmojis, setPartyEmojis] = useState([]);

  // New Year Wishes and Thoughts
  const newYearWishes = [
    "✨ New Year, New Beginnings!",
    "🌟 May Your Dreams Come True!",
    "💫 Wishing You Joy & Success!",
    "🎯 Chase Your Goals in 2026!",
    "❤️ Spread Love & Happiness!",
    "🚀 Reach New Heights!",
    "🌈 Make Every Moment Count!",
    "⭐ Shine Bright This Year!",
    "🎊 Celebrate Every Victory!",
    "💪 Stay Strong & Positive!"
  ];

  const celebrationWishes = [
    "🎆 Wishing You a Prosperous 2026!",
    "✨ May This Year Bring Endless Joy!",
    "🌟 New Adventures Await You!",
    "💫 Dream Big, Achieve Bigger!",
    "🎊 Cheers to New Beginnings!",
    "❤️ Filled with Love & Laughter!",
    "🚀 Sky is the Limit in 2026!",
    "🌈 Colors of Happiness for You!",
    "⭐ Be the Star You Are!",
    "💪 Your Best Year Yet!"
  ];

  const [currentWish, setCurrentWish] = useState(0);
  const [celebrationWish, setCelebrationWish] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
      
      if (newTimeLeft.expired && !isExpired) {
        setIsExpired(true);
        if (onComplete) onComplete();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [calculateTimeLeft, isExpired, onComplete]);

  // Rotate wishes
  useEffect(() => {
    const wishTimer = setInterval(() => {
      setCurrentWish(prev => (prev + 1) % newYearWishes.length);
    }, 3000);
    return () => clearInterval(wishTimer);
  }, [newYearWishes.length]);

  // Rotate celebration wishes
  useEffect(() => {
    const celebTimer = setInterval(() => {
      setCelebrationWish(prev => (prev + 1) % celebrationWishes.length);
    }, 4000);
    return () => clearInterval(celebTimer);
  }, [celebrationWishes.length]);

  // Party popup animation
  useEffect(() => {
    if (isExpired) return;
    
    const partyInterval = setInterval(() => {
      triggerPartyPopup();
    }, 10000);
    
    const initialTimeout = setTimeout(() => {
      triggerPartyPopup();
    }, 3000);

    return () => {
      clearInterval(partyInterval);
      clearTimeout(initialTimeout);
    };
  }, [isExpired]);

  const triggerPartyPopup = () => {
    const emojis = ['🎉', '🎊', '🥳', '🎈', '🎁', '✨', '💫', '🌟', '🎆', '🎇'];
    const newEmojis = [];
    
    for (let i = 0; i < 10; i++) {
      newEmojis.push({
        id: i,
        emoji: emojis[Math.floor(Math.random() * emojis.length)],
        left: 10 + Math.random() * 80,
        delay: Math.random() * 0.4,
        duration: 1.2 + Math.random() * 0.8,
        size: 12 + Math.random() * 10
      });
    }
    
    setPartyEmojis(newEmojis);
    setShowPartyPopup(true);
    
    setTimeout(() => {
      setShowPartyPopup(false);
    }, 2500);
  };

  // CELEBRATION VIEW - When it's New Year 2026
  if (isExpired) {
    return (
      <div className="new-year-celebration">
        <div className="celebration-bg">
          {/* Confetti burst */}
          <div className="confetti-container">
            {[...Array(40)].map((_, i) => (
              <div key={i} className={`confetti c-${i % 10}`} />
            ))}
          </div>
        </div>
        
        <div className="celebration-content">
          <div className="fireworks-row">
            <span className="fw">🎆</span>
            <span className="fw">🎇</span>
            <span className="fw">🎆</span>
          </div>
          
          <div className="happy-new-year">
            <span className="hny-text">Happy New Year</span>
            <span className="hny-year">2026</span>
          </div>
          
          <div className="celebration-wish" key={celebrationWish}>
            {celebrationWishes[celebrationWish]}
          </div>
          
          <div className="stars-row">
            <span>✦</span><span>✦</span><span>✦</span><span>✦</span><span>✦</span>
          </div>
        </div>

        <style>{`
          .new-year-celebration {
            position: fixed;
            bottom: 20px;
            right: 20px;
            width: 220px;
            background: linear-gradient(145deg, #0f0f25, #1a1a3e);
            border-radius: 16px;
            padding: 16px;
            box-shadow: 
              0 20px 50px rgba(0,0,0,0.5),
              0 0 0 1px rgba(255,215,0,0.3),
              0 0 40px rgba(255,215,0,0.1);
            z-index: 9998;
            overflow: hidden;
          }
          
          .celebration-bg {
            position: absolute;
            inset: 0;
            overflow: hidden;
          }
          
          .confetti-container {
            position: absolute;
            inset: 0;
          }
          
          .confetti {
            position: absolute;
            width: 8px;
            height: 8px;
            animation: confettiFall 3s linear infinite;
          }
          
          .c-0 { background: #FFD700; left: 5%; animation-delay: 0s; }
          .c-1 { background: #FF6B6B; left: 15%; animation-delay: 0.3s; }
          .c-2 { background: #4ECDC4; left: 25%; animation-delay: 0.6s; }
          .c-3 { background: #A855F7; left: 35%; animation-delay: 0.2s; }
          .c-4 { background: #F472B6; left: 45%; animation-delay: 0.5s; }
          .c-5 { background: #38BDF8; left: 55%; animation-delay: 0.1s; }
          .c-6 { background: #FFD700; left: 65%; animation-delay: 0.4s; }
          .c-7 { background: #FF6B6B; left: 75%; animation-delay: 0.7s; }
          .c-8 { background: #4ECDC4; left: 85%; animation-delay: 0.25s; }
          .c-9 { background: #A855F7; left: 95%; animation-delay: 0.55s; }
          
          @keyframes confettiFall {
            0% { top: -10px; transform: rotate(0deg); opacity: 1; }
            100% { top: 100%; transform: rotate(720deg); opacity: 0; }
          }
          
          .celebration-content {
            position: relative;
            z-index: 2;
            text-align: center;
          }
          
          .fireworks-row {
            display: flex;
            justify-content: center;
            gap: 10px;
            margin-bottom: 8px;
          }
          
          .fw {
            font-size: 1.3rem;
            animation: fwPop 1s infinite;
          }
          .fw:nth-child(2) { animation-delay: 0.3s; }
          .fw:nth-child(3) { animation-delay: 0.6s; }
          
          @keyframes fwPop {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.3); }
          }
          
          .happy-new-year {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: 10px;
          }
          
          .hny-text {
            font-size: 0.9rem;
            color: white;
            font-weight: 600;
            letter-spacing: 1px;
          }
          
          .hny-year {
            font-size: 2.2rem;
            font-weight: 900;
            background: linear-gradient(135deg, #FFD700, #FF6B6B, #4ECDC4);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            animation: yearGlow 2s infinite;
          }
          
          @keyframes yearGlow {
            0%, 100% { filter: brightness(1) drop-shadow(0 0 10px rgba(255,215,0,0.5)); }
            50% { filter: brightness(1.2) drop-shadow(0 0 20px rgba(255,215,0,0.8)); }
          }
          
          .celebration-wish {
            font-size: 0.7rem;
            color: #FFD700;
            padding: 8px 12px;
            background: rgba(255,215,0,0.1);
            border-radius: 20px;
            margin-bottom: 10px;
            animation: wishFade 4s ease-in-out;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          @keyframes wishFade {
            0% { opacity: 0; transform: translateY(5px); }
            10% { opacity: 1; transform: translateY(0); }
            90% { opacity: 1; }
            100% { opacity: 1; }
          }
          
          .stars-row {
            display: flex;
            justify-content: center;
            gap: 8px;
            color: #FFD700;
            font-size: 0.6rem;
          }
          
          .stars-row span {
            animation: starTwinkle 1.5s infinite;
          }
          .stars-row span:nth-child(2) { animation-delay: 0.3s; }
          .stars-row span:nth-child(3) { animation-delay: 0.6s; }
          .stars-row span:nth-child(4) { animation-delay: 0.9s; }
          .stars-row span:nth-child(5) { animation-delay: 1.2s; }
          
          @keyframes starTwinkle {
            0%, 100% { opacity: 0.4; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.3); }
          }
          
          @media (max-width: 480px) {
            .new-year-celebration {
              bottom: 10px;
              right: 10px;
              left: 10px;
              width: auto;
            }
          }
        `}</style>
      </div>
    );
  }

  // Minimized view
  if (isMinimized) {
    return (
      <button 
        onClick={() => setIsMinimized(false)}
        className="countdown-mini-btn"
      >
        <span className="mini-icon">🎆</span>
        <span className="mini-time">{timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m</span>
        <style>{`
          .countdown-mini-btn {
            position: fixed;
            bottom: 20px;
            right: 20px;
            background: linear-gradient(145deg, #1a1a3e, #0f0f2d);
            border: 2px solid rgba(255,215,0,0.4);
            padding: 10px 16px;
            border-radius: 30px;
            color: #FFD700;
            font-weight: bold;
            font-size: 0.8rem;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            box-shadow: 0 8px 25px rgba(0,0,0,0.4);
            transition: all 0.3s ease;
            z-index: 9998;
          }
          .countdown-mini-btn:hover {
            transform: scale(1.05);
            box-shadow: 0 12px 30px rgba(0,0,0,0.5);
          }
          .mini-icon {
            font-size: 1.1rem;
          }
        `}</style>
      </button>
    );
  }

  // Main countdown view - COMPACT
  return (
    <div className="countdown-compact">
      {/* Party Popup */}
      {showPartyPopup && (
        <div className="party-popup">
          {partyEmojis.map((item) => (
            <div
              key={item.id}
              className="party-emoji"
              style={{
                left: `${item.left}%`,
                animationDelay: `${item.delay}s`,
                animationDuration: `${item.duration}s`,
                fontSize: `${item.size}px`
              }}
            >
              {item.emoji}
            </div>
          ))}
        </div>
      )}

      <div className="countdown-inner">
        {/* Header with 2026 */}
        <div className="countdown-header">
          <span className="header-emoji">🎊</span>
          <span className="year-text">2026</span>
          <span className="header-emoji">🎊</span>
        </div>

        {/* Flashing Wishes */}
        <div className="wish-flash" key={currentWish}>
          {newYearWishes[currentWish]}
        </div>

        {/* Timer */}
        <div className="timer-row">
          <div className="time-box">
            <span className="time-val">{String(timeLeft.days).padStart(2, '0')}</span>
            <span className="time-lbl">D</span>
          </div>
          <span className="time-sep">:</span>
          <div className="time-box">
            <span className="time-val">{String(timeLeft.hours).padStart(2, '0')}</span>
            <span className="time-lbl">H</span>
          </div>
          <span className="time-sep">:</span>
          <div className="time-box">
            <span className="time-val">{String(timeLeft.minutes).padStart(2, '0')}</span>
            <span className="time-lbl">M</span>
          </div>
          <span className="time-sep">:</span>
          <div className="time-box sec-box">
            <span className="time-val">{String(timeLeft.seconds).padStart(2, '0')}</span>
            <span className="time-lbl">S</span>
          </div>
        </div>

        {/* Progress */}
        <div className="mini-progress">
          <div className="progress-bar" style={{ width: `${(60 - timeLeft.seconds) / 60 * 100}%` }}></div>
        </div>

        {/* Minimize btn */}
        <button className="min-btn" onClick={() => setIsMinimized(true)}>−</button>
        
        {/* Party btn */}
        <button className="party-btn" onClick={triggerPartyPopup}>🎉</button>
      </div>

      <style>{`
        .countdown-compact {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 240px;
          z-index: 9998;
        }

        .countdown-inner {
          background: linear-gradient(150deg, #0f0f25, #1a1a3e, #0d0d1f);
          border-radius: 16px;
          padding: 14px 16px 12px;
          box-shadow: 
            0 15px 40px rgba(0,0,0,0.5),
            0 0 0 1px rgba(255,215,0,0.2),
            inset 0 1px 0 rgba(255,255,255,0.1);
          position: relative;
          overflow: hidden;
        }

        .party-popup {
          position: absolute;
          inset: 0;
          overflow: hidden;
          z-index: 100;
          pointer-events: none;
        }

        .party-emoji {
          position: absolute;
          bottom: -15px;
          animation: emojiUp ease-out forwards;
        }

        @keyframes emojiUp {
          0% { transform: translateY(0) scale(0); opacity: 1; }
          100% { transform: translateY(-150px) scale(1) rotate(360deg); opacity: 0; }
        }

        .countdown-header {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-bottom: 8px;
        }

        .header-emoji {
          font-size: 1.1rem;
          animation: emojiBounce 2s infinite;
        }

        @keyframes emojiBounce {
          0%, 100% { transform: scale(1) rotate(-5deg); }
          50% { transform: scale(1.15) rotate(5deg); }
        }

        .year-text {
          font-size: 2rem;
          font-weight: 900;
          background: linear-gradient(180deg, #FFF 0%, #FFD700 50%, #FFA500 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          letter-spacing: 3px;
        }

        .wish-flash {
          text-align: center;
          font-size: 0.72rem;
          color: #FFD700;
          padding: 6px 10px;
          background: rgba(255,215,0,0.1);
          border-radius: 14px;
          margin-bottom: 12px;
          animation: wishPulse 3s ease-in-out;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        @keyframes wishPulse {
          0% { opacity: 0; transform: scale(0.95); }
          10% { opacity: 1; transform: scale(1); }
          90% { opacity: 1; }
          100% { opacity: 1; }
        }

        .timer-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
        }

        .time-box {
          display: flex;
          flex-direction: column;
          align-items: center;
          background: linear-gradient(180deg, #252545, #1a1a35);
          border-radius: 10px;
          padding: 8px 10px;
          min-width: 44px;
          border: 1px solid rgba(255,215,0,0.25);
          box-shadow: inset 0 2px 8px rgba(0,0,0,0.3);
        }

        .time-val {
          font-size: 1.3rem;
          font-weight: 800;
          color: #FFD700;
          font-family: 'Courier New', monospace;
          line-height: 1;
        }

        .time-lbl {
          font-size: 0.55rem;
          color: rgba(255,255,255,0.5);
          margin-top: 3px;
          letter-spacing: 1px;
        }

        .sec-box .time-val {
          animation: secPulse 1s infinite;
        }

        @keyframes secPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }

        .time-sep {
          color: #FFD700;
          font-size: 1.2rem;
          font-weight: bold;
          animation: sepBlink 1s infinite;
        }

        @keyframes sepBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        .mini-progress {
          height: 4px;
          background: rgba(255,255,255,0.1);
          border-radius: 2px;
          margin-top: 12px;
          overflow: hidden;
        }

        .progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #FFD700, #FF6B6B, #4ECDC4);
          border-radius: 2px;
          transition: width 1s linear;
        }

        .min-btn {
          position: absolute;
          top: -8px;
          right: -8px;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(145deg, #ff6b6b, #ee5252);
          border: 1px solid rgba(255,255,255,0.3);
          color: white;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
          padding-bottom: 2px;
          transition: transform 0.2s;
        }

        .min-btn:hover {
          transform: scale(1.15);
        }

        .party-btn {
          position: absolute;
          top: -8px;
          left: -8px;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: linear-gradient(145deg, #FFD700, #FFA500);
          border: 1px solid rgba(255,255,255,0.3);
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s;
          animation: partyPulse 2s infinite;
        }

        @keyframes partyPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        .party-btn:hover {
          transform: scale(1.2);
        }

        @media (max-width: 480px) {
          .countdown-compact {
            bottom: 10px;
            right: 10px;
            left: 10px;
            width: auto;
          }
          .countdown-inner {
            padding: 12px 14px 10px;
          }
          .year-text {
            font-size: 1.7rem;
          }
          .time-box {
            min-width: 38px;
            padding: 6px 8px;
          }
          .time-val {
            font-size: 1.1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CountdownTimer;
